import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';

const Navbar = () => {
  // State for mobile menu toggle
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // State for scrolled status
  const [scrolled, setScrolled] = useState(false);
  
  // State for window width
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  
  // Determine if mobile based on window width
  const isMobile = windowWidth < 768;
  
  // State for active section
  const [activeSection, setActiveSection] = useState('home');
  
  // Handle window resize
  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
      // Close mobile menu when resizing to desktop
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    }
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      handleResize();
      
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      // Update active section state
      setActiveSection(id);
    } else {
      console.error(`Element with id ${id} not found`);
    }
  };
  
  // Handle scroll events for navbar styling
  useEffect(() => {
    function handleScroll() {
      if (window.scrollY > 30) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
      
      // Detect active section based on scroll position
      const sections = ['home', 'services', 'about', 'contact'];
      let currentSection = activeSection; 

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          // If the top of the element is near the top of the viewport
          if (rect.top <= 100 && rect.bottom >= 100) {
            currentSection = section;
            break;
          }
        }
      }

      console.log(currentSection);
      if (currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    }
    
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  // Define all styles inline
  const styles = {
    navbar: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: scrolled ? '15px 30px' : '25px 30px',
      transition: 'all 0.3s ease',
      zIndex: 1000,
      backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
      backdropFilter: scrolled ? 'blur(10px)' : 'none',
      boxShadow: scrolled ? '0 2px 10px rgba(0, 0, 0, 0.08)' : 'none',
    },
    brand: {
      fontSize: '26px',
      fontWeight: 'bold',
      color: scrolled ? '#1e50a2' : '#1e50a2',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    logo: {
      width: '34px',
      height: '34px',
      backgroundColor: '#1e50a2',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    menuDesktop: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: '30px',
    },
    menuMobile: {
      display: isMobile ? 'flex' : 'none',
      flexDirection: 'column',
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      padding: isMenuOpen ? '20px 0' : '0',
      maxHeight: isMenuOpen ? '300px' : '0',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: isMenuOpen ? '0 10px 15px rgba(0, 0, 0, 0.1)' : 'none',
      borderBottomLeftRadius: '10px',
      borderBottomRightRadius: '10px',
    },
    menuItem: (isActive) => ({
      fontSize: '16px',
      color: isActive ? '#1e50a2' : '#4a5568',
      textDecoration: 'none',
      fontWeight: isActive ? '600' : '500',
      padding: '10px 5px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    }),
    menuItemMobile: (isActive) => ({
      fontSize: '16px',
      color: isActive ? '#1e50a2' : '#4a5568',
      textDecoration: 'none',
      fontWeight: isActive ? '600' : '500',
      padding: '15px 30px',
      borderBottom: '1px solid #f0f0f0',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
    }),
    menuIndicator: (isActive) => ({
      position: 'absolute',
      bottom: '0',
      left: '50%',
      width: isActive ? '20px' : '0',
      height: '2px',
      backgroundColor: '#1e50a2',
      transform: 'translateX(-50%)',
      transition: 'width 0.3s ease',
    }),
    cta: {
      padding: '10px 20px',
      backgroundColor: '#1e50a2',
      color: 'white',
      fontSize: '14px',
      fontWeight: '600',
      borderRadius: '30px',
      textDecoration: 'none',
      boxShadow: '0 4px 12px rgba(30, 80, 162, 0.3)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: 'none',
    },
    ctaMobile: {
      padding: '15px 30px',
      backgroundColor: '#1e50a2',
      color: 'white',
      fontSize: '14px',
      fontWeight: '600',
      borderRadius: '0',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      width: '100%',
      textAlign: 'center',
      border: 'none',
    },
    ctaHover: {
      backgroundColor: '#0a1438',
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 15px rgba(30, 80, 162, 0.4)',
    },
    hamburger: {
      display: isMobile ? 'flex' : 'none',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '24px',
      height: '18px',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
    },
    hamburgerLine: (isOpen, position) => {
      const styles = {
        width: '100%',
        height: '2px',
        backgroundColor: '#1e50a2',
        transition: 'all 0.3s ease',
      };
      
      if (isOpen) {
        if (position === 'top') {
          return {
            ...styles,
            transform: 'translateY(8px) rotate(45deg)',
          };
        } else if (position === 'middle') {
          return {
            ...styles,
            opacity: 0,
            transform: 'translateX(-10px)',
          };
        } else if (position === 'bottom') {
          return {
            ...styles,
            transform: 'translateY(-8px) rotate(-45deg)',
          };
        }
      }
      
      return styles;
    }
  };
  
  // State for hover effects
  const [hoveredItem, setHoveredItem] = useState(null);
  
  return (
    <nav style={styles.navbar}>
      {/* Brand/Logo */}
      <a href="/" style={styles.brand}>
        <div style={styles.logo}>L</div>
        Laki
      </a>
      
      {/* Desktop Menu */}
      <div style={styles.menuDesktop}>
        <div 
          to="home" 
          smooth={true} 
          duration={800}
          style={{
            ...styles.menuItem(activeSection === 'home'),
            ...(hoveredItem === 'home' && !isMobile ? styles.ctaHover : {})
          }}
          onMouseEnter={() => setHoveredItem('home')}
          onMouseLeave={() => setHoveredItem(null)}
          onClick={() => scrollToSection('home')}
        >
          Home
          <div style={styles.menuIndicator(activeSection === 'home')}></div>
        </div>
        
        <div 
          to="services"
          spy={true} 
          smooth={true} 
          duration={800}
          offset={-70} // Add this line - adjust the value based on your navbar height
          style={{
            ...styles.menuItem(activeSection === 'services'),
            ...(hoveredItem === 'services' && !isMobile ? styles.ctaHover : {})
          }}
          onMouseEnter={() => setHoveredItem('services')}
          onMouseLeave={() => setHoveredItem(null)}
          onClick={() => scrollToSection('services')}
        >
          How it Works
          <div style={styles.menuIndicator(activeSection === 'services')}></div>
        </div>
        
        <div 
          to="about" 
          smooth={true} 
          duration={800}
          style={{
            ...styles.menuItem(activeSection === 'about'),
            ...(hoveredItem === 'about' && !isMobile ? styles.ctaHover : {})
          }}
          onMouseEnter={() => setHoveredItem('about')}
          onMouseLeave={() => setHoveredItem(null)}
          onClick={() => scrollToSection('about')}
        >
          About
          <div style={styles.menuIndicator(activeSection === 'about')}></div>
        </div>
        
        <div 
          to="contact" 
          smooth={true} 
          duration={800}
          style={{
            ...styles.menuItem(activeSection === 'contact'),
            ...(hoveredItem === 'contact' && !isMobile ? styles.ctaHover : {})
          }}
          onMouseEnter={() => setHoveredItem('contact')}
          onMouseLeave={() => setHoveredItem(null)}
          onClick={() => scrollToSection('contact')}
        >
          Contact
          <div style={styles.menuIndicator(activeSection === 'contact')}></div>
        </div>
        
        <button 
          style={{
            ...styles.cta,
            ...(hoveredItem === 'cta' ? styles.ctaHover : {})
          }}
          onMouseEnter={() => setHoveredItem('cta')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          Open your account
        </button>
      </div>
      
      {/* Mobile Hamburger Menu Button */}
      <button 
        style={styles.hamburger}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle menu"
      >
        <span style={styles.hamburgerLine(isMenuOpen, 'top')}></span>
        <span style={styles.hamburgerLine(isMenuOpen, 'middle')}></span>
        <span style={styles.hamburgerLine(isMenuOpen, 'bottom')}></span>
      </button>
      
      {/* Mobile Menu */}
      <div style={styles.menuMobile}>
        <Link 
          to="home" 
          smooth={true} 
          duration={800}
          style={styles.menuItemMobile(activeSection === 'home')}
          onClick={() => setIsMenuOpen(false)}
        >
          Home
        </Link>
        
        <Link 
          to="services" 
          smooth={true} 
          duration={800}
          style={styles.menuItemMobile(activeSection === 'services')}
          onClick={() => setIsMenuOpen(false)}
        >
          How it Works
        </Link>
        
        <Link 
          to="about" 
          smooth={true} 
          duration={800}
          style={styles.menuItemMobile(activeSection === 'about')}
          onClick={() => setIsMenuOpen(false)}
        >
          About
        </Link>
        
        <Link 
          to="contact" 
          smooth={true} 
          duration={800}
          style={styles.menuItemMobile(activeSection === 'contact')}
          onClick={() => setIsMenuOpen(false)}
        >
          Contact
        </Link>
        
        <button 
          style={styles.ctaMobile}
          onClick={() => setIsMenuOpen(false)}
        >
          Open your account
        </button>
      </div>
    </nav>
  );
};

export default Navbar;